import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AppServices } from 'middleware'
import { ActualRow, AsServedRow, ForecastCadence, ForecastRow, ForecastTarget, HolidayRow, OnBooksRow, VenueCalendarRow, WeatherRow, HindcastRow } from './pythiaService'

export interface ForecastsParams {
    accountSlug: string
    target: ForecastTarget
    cadence: ForecastCadence
}

export interface WeatherParams {
    accountSlug: string
    days: number
    pastDays?: number
}

export interface HolidaysParams {
    accountSlug: string
    days: number
}

export interface ActualsParams {
    accountSlug: string
    target: ForecastTarget
    dateFrom: string
    dateTo: string
    includeIncomplete?: boolean
}

export interface OnBooksParams {
    accountSlug: string
    days: number
}

export interface ProductSplit {
    products: Array<{ id: string; name: string; value: number }>
    total: number
    unsplit: boolean
    unavailable: boolean
}

export const pulseApi = createApi({
    reducerPath: 'pulseApi',
    baseQuery: fetchBaseQuery({}),
    refetchOnMountOrArgChange: true,
    endpoints: () => ({}),
})

const extendedPulseApi = pulseApi.injectEndpoints({
    endpoints: (builder) => ({
        getForecasts: builder.query<ForecastRow[], ForecastsParams>({
            queryFn: async ({ accountSlug, target, cadence }) => {
                const data = await AppServices.pythiaService.getForecasts({ account: accountSlug, target, cadence })
                return { data }
            },
        }),
        getWeather: builder.query<WeatherRow[], WeatherParams>({
            queryFn: async ({ accountSlug, days, pastDays }) => {
                const data = await AppServices.pythiaService.getWeather({ account: accountSlug, days, pastDays })
                return { data }
            },
        }),
        getHolidays: builder.query<HolidayRow[], HolidaysParams>({
            queryFn: async ({ accountSlug, days }) => {
                const data = await AppServices.pythiaService.getHolidays({ account: accountSlug, days })
                return { data }
            },
        }),
        getVenueCalendar: builder.query<VenueCalendarRow[], { accountSlug: string; dateFrom: string; dateTo: string }>({
            queryFn: async ({ accountSlug, dateFrom, dateTo }) => {
                const data = await AppServices.pythiaService.getVenueCalendar({ account: accountSlug, dateFrom, dateTo })
                return { data }
            },
        }),
        getHindcasts: builder.query<HindcastRow[], { accountSlug: string; target: ForecastTarget; dateFrom: string; dateTo: string }>({
            queryFn: async ({ accountSlug, target, dateFrom, dateTo }) => {
                const data = await AppServices.pythiaService.getHindcasts({
                    account: accountSlug,
                    target,
                    cadence: 'daily',
                    dateFrom,
                    dateTo,
                })
                return { data }
            },
        }),
        getAsServed: builder.query<AsServedRow[], { accountSlug: string; target: ForecastTarget; dateFrom: string; dateTo: string }>({
            queryFn: async ({ accountSlug, target, dateFrom, dateTo }) => {
                const data = await AppServices.pythiaService.getAsServed({
                    account: accountSlug,
                    target,
                    cadence: 'daily',
                    dateFrom,
                    dateTo,
                })
                return { data }
            },
        }),
        getActuals: builder.query<ActualRow[], ActualsParams>({
            queryFn: async ({ accountSlug, target, dateFrom, dateTo, includeIncomplete }) => {
                const data = await AppServices.pythiaService.getActuals({
                    account: accountSlug,
                    target,
                    cadence: 'daily',
                    dateFrom,
                    dateTo,
                    includeIncomplete,
                })
                return { data }
            },
        }),
        getOnBooks: builder.query<OnBooksRow[], OnBooksParams>({
            queryFn: async ({ accountSlug, days }) => {
                const data = await AppServices.pythiaService.getOnBooks({ account: accountSlug, days })
                return { data }
            },
        }),
        getVisitorsByProduct: builder.query<ProductSplit, { accountSlug: string; date: string }>({
            queryFn: async ({ accountSlug, date }) => {
                const { rows, truncated } = await AppServices.pythiaService.getVisitorsByProduct({
                    account: accountSlug,
                    date,
                })
                if (truncated) {
                    console.error(`Pulse: truncated product split for ${accountSlug} ${date}`)
                    return { data: { products: [], total: 0, unsplit: false, unavailable: true } }
                }
                const total = rows.reduce((s, r) => s + (r.value ?? 0), 0)
                const byProduct = new Map<string, { id: string; name: string; value: number }>()
                for (const r of rows) {
                    if (r.value == null || r.product_id == null) continue
                    const entry = byProduct.get(r.product_id)
                    if (entry) {
                        entry.value += r.value
                    } else {
                        byProduct.set(r.product_id, { id: r.product_id, name: r.product_name ?? r.product_id, value: r.value })
                    }
                }
                const products = [...byProduct.values()].sort((a, b) => b.value - a.value)
                return {
                    data: {
                        products,
                        total,
                        unsplit: products.length === 0 && total > 0,
                        unavailable: false,
                    },
                }
            },
        }),
    }),
})

export const { reducer, reducerPath } = pulseApi
export const {
    useGetForecastsQuery,
    useGetWeatherQuery,
    useGetHolidaysQuery,
    useGetVenueCalendarQuery,
    useGetActualsQuery,
    useGetHindcastsQuery,
    useGetAsServedQuery,
    useGetOnBooksQuery,
    useGetVisitorsByProductQuery,
} = extendedPulseApi
export const endpoints = extendedPulseApi.endpoints
